import React, { useState, useEffect } from 'react';
import { Edit3, Save, X } from './Icons';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';

const AnnouncementBar = () => {
  const { user } = useAuth();
  const [announcement, setAnnouncement] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [loading, setLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [animationKey, setAnimationKey] = useState(0);

  // Parse announcement text to convert [link]text[link] to clickable links
  const parseAnnouncementText = (text) => {
    const linkRegex = /\[link\](.*?)\[link\]/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = linkRegex.exec(text)) !== null) {
      // Add text before the link
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: text.slice(lastIndex, match.index)
        });
      }
      
      // Add the link
      parts.push({
        type: 'link',
        content: match[1],
        url: match[1].startsWith('http') ? match[1] : `https://${match[1]}`
      });
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < text.length) {
      parts.push({
        type: 'text',
        content: text.slice(lastIndex)
      });
    }
    
    return parts;
  };

  useEffect(() => {
    fetchAnnouncement();
  }, []);


  const fetchAnnouncement = async () => {
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'announcement_text')
        .single();

      if (error) {
        if (error.code === '42P01') {
          console.log('site_settings table does not exist yet, checking localStorage');
          // Fallback to localStorage
          const storedText = localStorage.getItem('announcement_text');
          if (storedText) {
            setAnnouncement(storedText);
          } else {
            setAnnouncement('Over 14,250 players online right now! Weekend tournament registration now open!');
          }
          setIsInitialLoading(false);
          return;
        }
        if (error.code !== 'PGRST116') {
          console.error('Error fetching announcement:', error);
          setAnnouncement('Over 14,250 players online right now! Weekend tournament registration now open!');
          setIsInitialLoading(false);
          return;
        }
      }

      if (data?.value) {
        setAnnouncement(data.value);
      } else {
        // Set default if no data found
        setAnnouncement('Over 14,250 players online right now! Weekend tournament registration now open!');
      }
      setIsInitialLoading(false);
    } catch (error) {
      console.error('Error fetching announcement:', error);
      // Fallback to localStorage if database fails
      const storedText = localStorage.getItem('announcement_text');
      if (storedText) {
        setAnnouncement(storedText);
      } else {
        setAnnouncement('Over 14,250 players online right now! Weekend tournament registration now open!');
      }
      setIsInitialLoading(false);
    }
  };

  const handleEdit = () => {
    setEditText(announcement);
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!editText.trim()) {
      toast.error('Announcement text cannot be empty');
      return;
    }

    setLoading(true);
    
    try {
      // Try to update the existing record
      const { data: updateData, error: updateError } = await supabase
        .from('site_settings')
        .update({ value: editText.trim() })
        .eq('key', 'announcement_text')
        .select();
      
      // If update failed because record doesn't exist, try insert
      if (updateError || !updateData || updateData.length === 0) {
        const { error: insertError } = await supabase
          .from('site_settings')
          .insert({ key: 'announcement_text', value: editText.trim() });
        
        if (insertError) {
          throw insertError;
        }
      } else if (updateError) {
        throw updateError;
      }

      // Update the announcement text
      const newText = editText.trim();
      setAnnouncement(newText);
      setIsEditing(false);
      
      // Force animation restart by changing the key
      setAnimationKey(prev => prev + 1);
      
      toast.success('Announcement updated successfully!');
    } catch (error) {
      console.error('Error saving announcement:', error);
      
      // Fallback to localStorage if database fails
      const newText = editText.trim();
      localStorage.setItem('announcement_text', newText);
      setAnnouncement(newText);
      setIsEditing(false);
      setAnimationKey(prev => prev + 1);
      toast.success('Announcement updated successfully! (Saved locally)');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditText('');
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  // Don't render while loading or if no announcement text
  if (isInitialLoading || !announcement) return null;

  return (
    <div className="backdrop-blur-md bg-black/60 border-b border-white/10 overflow-hidden relative z-20">
      <div className="max-w-screen-2xl mx-auto px-3 sm:px-4">
        <div className="relative h-8 flex items-center">
          {/* Rolling text animation */}
          <div className="absolute inset-0 flex items-center overflow-hidden">
            <div 
              key={`${announcement}-${animationKey}`} 
              className={`${announcement.length < 50 ? 'animate-scroll-short' : 'animate-scroll'} whitespace-nowrap text-xs text-white/90 font-medium flex items-center`}
            >
              {parseAnnouncementText(announcement).map((part, index) => (
                part.type === 'link' ? (
                  <a
                    key={index}
                    href={part.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-300 hover:text-blue-200 underline transition-colors duration-200"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {part.content}
                  </a>
                ) : (
                  <span key={index}>{part.content}</span>
                )
              ))}
            </div>
          </div>

          {/* Admin edit button */}
          {user?.isAdmin && !isEditing && (
            <button
              onClick={handleEdit}
              className="absolute right-0 z-10 p-1.5 bg-white/20 hover:bg-white/30 rounded-md transition-colors duration-200 group"
              title="Edit announcement"
            >
              <Edit3 className="w-3 h-3 text-white/80 group-hover:text-white group-hover:scale-110 transition-all" />
            </button>
          )}

          {/* Edit mode */}
          {isEditing && (
            <div className="absolute inset-0 backdrop-blur-md bg-white/90 dark:bg-zinc-800/90 flex items-center gap-2 z-20">
              <input
                type="text"
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                onKeyDown={handleKeyDown}
                className="flex-1 bg-white/50 dark:bg-zinc-700/50 border border-white/30 dark:border-zinc-600/30 rounded-md px-2 py-1 text-white placeholder-gray-500/70 dark:placeholder-gray-400/70 focus:outline-none focus:ring-2 focus:ring-white/50 text-xs backdrop-blur-sm"
                placeholder="Enter announcement text..."
                autoFocus
              />
              <button
                onClick={handleSave}
                disabled={loading}
                className="p-1 bg-green-500/80 hover:bg-green-600/80 disabled:bg-green-500/50 rounded-md transition-colors duration-200 text-white backdrop-blur-sm"
                title="Save"
              >
                <Save className="w-3 h-3" />
              </button>
              <button
                onClick={handleCancel}
                disabled={loading}
                className="p-1 bg-white/30 hover:bg-white/40 disabled:bg-white/20 rounded-md transition-colors duration-200 text-white backdrop-blur-sm"
                title="Cancel"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnnouncementBar;